import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import { Menu, X, Calendar, ClipboardList, User, LogOut, Shield, Settings, Mail, Users, GraduationCap, Briefcase } from "lucide-react";
import { useAuth, hasAdminRole } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Header = ({ pageTitle }) => {
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hasApprovedEvents, setHasApprovedEvents] = useState(false);

  // Single source of truth for auth state. AuthContext fetches /auth/me on
  // mount when user_token exists; Header reads roles + hosted_event from
  // the same response instead of refetching.
  const { user, logout } = useAuth();
  const isLoggedIn = !!user;
  const isAdmin = hasAdminRole(user);
  const hostedEvent = user?.hosted_event || null;

  // Persist host token so the host-dashboard link works after a hard refresh
  // without needing the magic-link email. The /auth/me response in context
  // includes a fresh host_token; mirror it into localStorage on changes.
  useEffect(() => {
    if (hostedEvent?.host_token) {
      localStorage.setItem("host_token", hostedEvent.host_token);
    }
  }, [hostedEvent?.host_token]);

  // Check if user has any approved hosted events for the menu link.
  // This calls a different endpoint from /auth/me so it stays separate.
  useEffect(() => {
    if (!isLoggedIn) {
      setHasApprovedEvents(false);
      return;
    }
    const token = localStorage.getItem("user_token");
    api
      .get(`${BACKEND_URL}/api/auth/my-hosted-events`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => {
        const events = res.data.events || [];
        if (events.some(e => e.approval_status === "approved")) {
          setHasApprovedEvents(true);
        }
      })
      .catch(() => {});
  }, [isLoggedIn]);

  const handleLogout = () => {
    // logout() from AuthContext clears user_token + user state. Mirror the
    // other localStorage keys this component used to clean up before the
    // unified-auth refactor (defensive — they're stale by now).
    logout();
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_email");
    localStorage.removeItem("user");
    localStorage.removeItem("host_token");
    setMenuOpen(false);
    navigate("/");
    window.location.reload();
  };

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        document.documentElement.style.setProperty('--header-height', `${headerRef.current.offsetHeight}px`);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Check if this is a dev/preview environment
  const isDevEnvironment = window.location.hostname.includes('preview.emergentagent.com');

  return (
    <>
      {/* Navigation Bar */}
      <div ref={headerRef} style={{
        background: "linear-gradient(135deg, #1B4D3E 0%, #2D6A4F 100%)",
        padding: "0.75rem 1rem",
        position: "sticky",
        top: 0,
        zIndex: 100,
        borderBottom: isDevEnvironment ? "3px solid #D32F2F" : "none",
        ...(process.env.REACT_APP_ENV === "staging" ? { outline: "4px solid red" } : {})
      }}>
        <div style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          {/* Hamburger Menu Button - Left */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              background: "transparent",
              border: "none",
              color: "#FFF",
              padding: "0.4rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center"
            }}
            aria-label="Menu"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Site Name - Centered */}
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: "0.5rem", textDecoration: "none" }}>
            <span style={{ fontSize: "1.5rem" }}>🀄</span>
            <span style={{ color: "#FFF", fontWeight: "700", fontSize: "1.15rem" }}>
              PlayMahjong.in<sub style={{ fontSize: "0.55rem", fontWeight: "500", opacity: 0.8, marginLeft: "2px" }}>beta</sub>
            </span>
          </Link>

          {/* Spacer for balance */}
          <div style={{ width: "32px" }}></div>
        </div>
        
        {/* Site Description */}
        <div style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "0.15rem 1rem 0.5rem",
          textAlign: "center"
        }}>
          <p style={{
            color: "rgba(255,255,255,0.95)",
            fontSize: "0.8rem",
            margin: 0
          }}>
            Find and join local Mahjong games in your area
          </p>
        </div>
      </div>

      {/* Dropdown Menu */}
      {menuOpen && (
        <>
          {/* Backdrop */}
          <div 
            onClick={closeMenu}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.3)",
              zIndex: 98
            }}
          />
          
          {/* Menu Panel */}
          <div style={{
            position: "fixed",
            top: "55px",
            left: "1rem",
            background: "#FFF",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            minWidth: "200px",
            zIndex: 101,
            overflow: "hidden"
          }}>
            <div style={{ padding: "0.5rem 0" }}>
              {/* Primary Navigation */}
              <Link 
                to="/" 
                onClick={closeMenu}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  color: "#1B4D3E",
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  fontWeight: "500"
                }}
              >
                <Calendar size={18} />
                Events
              </Link>
              
              <Link 
                to="/teachers"
                onClick={closeMenu}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  color: "#1B4D3E",
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  fontWeight: "500"
                }}
              >
                <GraduationCap size={18} />
                Find a Teacher
              </Link>
              
              <a 
                href="https://chat.whatsapp.com/LgawyQzg5jb3YGDQkRiEaO?mode=gi_t"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMenu}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  color: "#25D366",
                  textDecoration: "none",
                  fontSize: "0.9rem",
                  fontWeight: "500"
                }}
              >
                <Users size={18} />
                Community
              </a>
              
              <div style={{ borderTop: "1px solid #E2E2DC", margin: "0.5rem 0" }} />
              
              {/* User Account Section */}
              {isLoggedIn ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={closeMenu}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      padding: "0.75rem 1rem",
                      color: "#1B4D3E",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      fontWeight: "500"
                    }}
                  >
                    <ClipboardList size={18} />
                    My Signups
                  </Link>

                  {hasApprovedEvents && (
                    <Link
                      to="/host/my-events"
                      onClick={closeMenu}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        padding: "0.75rem 1rem",
                        color: "#1B4D3E",
                        textDecoration: "none",
                        fontSize: "0.9rem",
                        fontWeight: "500"
                      }}
                    >
                      <Briefcase size={18} />
                      Manage your events
                    </Link>
                  )}

                  <Link
                    to="/account"
                    onClick={closeMenu}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      padding: "0.75rem 1rem",
                      color: "#1B4D3E",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      fontWeight: "500"
                    }}
                  >
                    <User size={18} />
                    Account
                  </Link>
                  
                  <button 
                    onClick={handleLogout}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      padding: "0.75rem 1rem",
                      color: "#D32F2F",
                      background: "transparent",
                      border: "none",
                      fontSize: "0.9rem",
                      fontWeight: "500",
                      width: "100%",
                      cursor: "pointer",
                      textAlign: "left"
                    }}
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </>
              ) : (
                <Link 
                  to="/login" 
                  onClick={closeMenu}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 1rem",
                    color: "#1B4D3E",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    fontWeight: "500"
                  }}
                >
                  <User size={18} />
                  Login / Sign Up
                </Link>
              )}
              
              {/* Admin Section - shown when logged in as admin */}
              {isAdmin && (
                <>
                  <div style={{ borderTop: "1px solid #E2E2DC", margin: "0.5rem 0" }} />
                  <div style={{ padding: "0.25rem 1rem", fontSize: "0.7rem", color: "#888", textTransform: "uppercase", fontWeight: "600" }}>
                    Admin
                  </div>
                  <Link 
                    to="/admin/dashboard" 
                    onClick={closeMenu}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      padding: "0.6rem 1rem",
                      color: "#1B4D3E",
                      textDecoration: "none",
                      fontSize: "0.85rem",
                      fontWeight: "500"
                    }}
                  >
                    <Shield size={16} />
                    Dashboard
                  </Link>
                  <Link
                    to="/admin/settings"
                    onClick={closeMenu}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                      padding: "0.6rem 1rem",
                      color: "#1B4D3E",
                      textDecoration: "none",
                      fontSize: "0.85rem",
                      fontWeight: "500"
                    }}
                  >
                    <Settings size={16} />
                    Settings
                  </Link>
                  {/* Manage Admins lives in the AdminNav dropdown's System
                      section now; super_admin gating is enforced there too. */}
                </>
              )}
              
              {/* Contact - Always at bottom */}
              <div style={{ borderTop: "1px solid #E2E2DC", margin: "0.5rem 0" }} />
              <a 
                href="mailto:contact@playmahjong.in"
                onClick={closeMenu}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.6rem 1rem",
                  color: "#888",
                  textDecoration: "none",
                  fontSize: "0.8rem",
                  fontWeight: "500"
                }}
              >
                <Mail size={16} />
                Contact Us
              </a>
            </div>
          </div>
        </>
      )}

      {/* Page Title */}
      {pageTitle && (
        <div className="container" style={{ paddingTop: "0.75rem", paddingBottom: "0.5rem" }}>
          <h1 style={{
            color: "#1B4D3E",
            fontSize: "1.25rem",
            fontWeight: "700",
            margin: 0
          }}>
            {pageTitle}
          </h1>
        </div>
      )}
    </>
  );
};

export default Header;
